import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Target, 
  Newspaper, 
  Users,
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  Gift,
  Loader2,
  Trophy,
  Save,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAnimation = () => {
  const { isSA } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [news, setNews] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [rhUsers, setRhUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Challenge modal
  const [isChallengeOpen, setIsChallengeOpen] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [challengeForm, setChallengeForm] = useState({
    title: '',
    description: '',
    target: '',
    reward: '',
    start_date: '',
    end_date: '',
    target_type: 'all',
    target_roles: [],
    target_agency_id: ''
  });
  
  // News modal
  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    category: 'general',
    is_important: false,
    // Targeting
    target_type: 'all', // 'all', 'roles', 'rh_commercials', 'specific'
    target_roles: [],
    target_rh_commercials: '',
    target_users: [],
    target_agency_id: ''
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, newsRes, usersRes] = await Promise.all([
        fetch(`${API}/challenges`, { credentials: 'include' }),
        fetch(`${API}/news`, { credentials: 'include' }),
        fetch(`${API}/admin/users`, { credentials: 'include' }),
      ]);
      
      if (challengesRes.ok) setChallenges(await challengesRes.json());
      if (newsRes.ok) setNews(await newsRes.json());
      if (usersRes.ok) {
        const fetchedUsers = await usersRes.json();
        setAllUsers(fetchedUsers);
        setUsers(fetchedUsers.filter(u => u.role === 'commercial'));
        setRhUsers(fetchedUsers.filter(u => u.role === 'rh'));
      }
      if (isSA) {
        const agRes = await fetch(`${API}/agencies`, { credentials: 'include' });
        if (agRes.ok) setAgencies(await agRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Challenge handlers
  const openNewChallenge = () => {
    setEditingChallenge(null);
    setChallengeForm({
      title: '',
      description: '',
      target: '',
      reward: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      target_type: 'all',
      target_roles: [],
      target_agency_id: ''
    });
    setIsChallengeOpen(true);
  };

  const openEditChallenge = (challenge) => {
    setEditingChallenge(challenge);
    setChallengeForm({
      title: challenge.title,
      description: challenge.description,
      target: challenge.target.toString(),
      reward: challenge.reward,
      start_date: challenge.start_date?.split('T')[0] || '',
      end_date: challenge.end_date?.split('T')[0] || '',
      target_type: challenge.target_roles?.length ? 'roles' : 'all',
      target_roles: challenge.target_roles || [],
      target_agency_id: challenge.agency_id || ''
    });
    setIsChallengeOpen(true);
  };

  const saveChallenge = async () => {
    if (!challengeForm.title || !challengeForm.target || !challengeForm.end_date) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: challengeForm.title,
        description: challengeForm.description,
        target: parseFloat(challengeForm.target),
        reward: challengeForm.reward,
        start_date: challengeForm.start_date,
        end_date: challengeForm.end_date,
        target_roles: challengeForm.target_type === 'roles' ? challengeForm.target_roles : null,
        target_agency_id: isSA && challengeForm.target_agency_id ? challengeForm.target_agency_id : null
      };
      
      const url = editingChallenge 
        ? `${API}/admin/challenges/${editingChallenge.challenge_id}`
        : `${API}/admin/challenges`;
      
      const response = await fetch(url, {
        method: editingChallenge ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success(editingChallenge ? 'Challenge mis a jour' : 'Challenge cree');
        setIsChallengeOpen(false);
        fetchData();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const deleteChallenge = async (challengeId) => {
    if (!confirm('Supprimer ce challenge ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/challenges/${challengeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Challenge supprime');
        fetchData();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // News handlers
  const openNewNews = () => {
    setEditingNews(null);
    setNewsForm({ 
      title: '', 
      content: '', 
      priority: 'normal',
      category: 'general',
      is_important: false,
      target_type: 'all',
      target_roles: [],
      target_rh_commercials: '',
      target_users: [],
      target_agency_id: ''
    });
    setIsNewsOpen(true);
  };

  const openEditNews = (item) => {
    setEditingNews(item);
    setNewsForm({
      title: item.title,
      content: item.content,
      priority: item.priority || 'normal',
      category: item.category || 'general',
      is_important: item.is_important || false,
      target_type: item.target_roles?.length ? 'roles' : 
                   item.target_rh_commercials ? 'rh_commercials' : 
                   item.target_users?.length ? 'specific' : 'all',
      target_roles: item.target_roles || [],
      target_rh_commercials: item.target_rh_commercials || '',
      target_users: item.target_users || [],
      target_agency_id: item.agency_id || ''
    });
    setIsNewsOpen(true);
  };

  const saveNews = async () => {
    if (!newsForm.title || !newsForm.content) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setSaving(true);
    try {
      // Build payload based on target_type
      let payload = {
        title: newsForm.title,
        content: newsForm.content,
        category: newsForm.category,
        is_important: newsForm.is_important,
        target_roles: null,
        target_users: null,
        target_rh_commercials: null,
        target_agency_id: isSA && newsForm.target_agency_id ? newsForm.target_agency_id : null
      };
      
      if (newsForm.target_type === 'roles') {
        payload.target_roles = newsForm.target_roles;
      } else if (newsForm.target_type === 'rh_commercials') {
        payload.target_rh_commercials = newsForm.target_rh_commercials;
      } else if (newsForm.target_type === 'specific') {
        payload.target_users = newsForm.target_users;
      }
      
      // Use new endpoint /api/news for targeting support
      const url = editingNews
        ? `${API}/admin/news/${editingNews.news_id}`
        : `${API}/admin/news`;
      
      const response = await fetch(url, {
        method: editingNews ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingNews ? { ...payload, priority: newsForm.priority } : payload),
      });
      
      if (response.ok) {
        toast.success(editingNews ? 'Actualite mise a jour' : 'Actualite publiee');
        setIsNewsOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setNewsForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const toggleUser = (userId) => {
    setNewsForm(prev => ({
      ...prev,
      target_users: prev.target_users.includes(userId)
        ? prev.target_users.filter(u => u !== userId)
        : [...prev.target_users, userId]
    }));
  };

  const deleteNews = async (newsId) => {
    if (!confirm('Supprimer cette actualite ?')) return;
    
    try {
      const response = await fetch(`${API}/admin/news/${newsId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Actualite supprimee');
        fetchData();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]">
          Gestion Animation
        </h1>
        <p className="text-slate-500 mt-1">Challenges, actualites et objectifs</p>
      </div>

      <Tabs defaultValue="challenges">
        <TabsList>
          <TabsTrigger value="challenges" className="flex items-center gap-2">
            <Target className="w-4 h-4" /> Challenges
          </TabsTrigger>
          <TabsTrigger value="news" className="flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> Actualites
          </TabsTrigger>
        </TabsList>

        {/* Challenges Tab */}
        <TabsContent value="challenges" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Challenges</CardTitle>
                <CardDescription>Gerez les challenges commerciaux</CardDescription>
              </div>
              <Button onClick={openNewChallenge}>
                <Plus className="w-4 h-4 mr-2" /> Nouveau challenge
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Objectif</TableHead>
                    <TableHead>Recompense</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {challenges.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        Aucun challenge
                      </TableCell>
                    </TableRow>
                  ) : (
                    challenges.map((challenge) => (
                      <TableRow key={challenge.challenge_id}>
                        <TableCell className="font-medium">{challenge.title}</TableCell>
                        <TableCell>{challenge.target?.toLocaleString('fr-FR')} EUR</TableCell>
                        <TableCell>{challenge.reward}</TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {formatDate(challenge.start_date)} - {formatDate(challenge.end_date)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditChallenge(challenge)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteChallenge(challenge.challenge_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* News Tab */}
        <TabsContent value="news" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Actualites</CardTitle>
                <CardDescription>Publiez des annonces pour l'equipe</CardDescription>
              </div>
              <Button onClick={openNewNews}>
                <Plus className="w-4 h-4 mr-2" /> Nouvelle actualite
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Priorite</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {news.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        Aucune actualite
                      </TableCell>
                    </TableRow>
                  ) : (
                    news.map((item) => (
                      <TableRow key={item.news_id}>
                        <TableCell className="font-medium">{item.title}</TableCell>
                        <TableCell>
                          <Badge className={
                            item.priority === 'high' ? 'bg-red-100 text-red-700' :
                            item.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {item.priority === 'high' ? 'Haute' : item.priority === 'normal' ? 'Normale' : 'Basse'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">{formatDate(item.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditNews(item)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteNews(item.news_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Challenge Modal */}
      <Dialog open={isChallengeOpen} onOpenChange={setIsChallengeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingChallenge ? 'Modifier le challenge' : 'Nouveau challenge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={challengeForm.title}
                onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })}
                placeholder="Ex: Challenge du mois"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={challengeForm.description}
                onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })}
                placeholder="Description du challenge..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Objectif (EUR) *</Label>
                <Input
                  type="number"
                  value={challengeForm.target}
                  onChange={(e) => setChallengeForm({ ...challengeForm, target: e.target.value })}
                  placeholder="10000"
                />
              </div>
              <div className="space-y-2">
                <Label>Recompense</Label>
                <Input
                  value={challengeForm.reward}
                  onChange={(e) => setChallengeForm({ ...challengeForm, reward: e.target.value })}
                  placeholder="Ex: 500EUR bonus"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de debut</Label>
                <Input
                  type="date"
                  value={challengeForm.start_date}
                  onChange={(e) => setChallengeForm({ ...challengeForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin *</Label>
                <Input
                  type="date"
                  value={challengeForm.end_date}
                  onChange={(e) => setChallengeForm({ ...challengeForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Send className="w-4 h-4" /> Destinataires
              </Label>
              {isSA && (
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Filiale cible</Label>
                  <Select value={challengeForm.target_agency_id || 'all'} onValueChange={(v) => setChallengeForm({ ...challengeForm, target_agency_id: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="challenge-agency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les filiales</SelectItem>
                      {agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Select value={challengeForm.target_type} onValueChange={(v) => setChallengeForm({ ...challengeForm, target_type: v, target_roles: [] })}>
                <SelectTrigger data-testid="challenge-target-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isSA ? 'Tous les utilisateurs (de la cible)' : 'Tous les utilisateurs de ma filiale'}</SelectItem>
                  <SelectItem value="roles">Par role</SelectItem>
                </SelectContent>
              </Select>
              {challengeForm.target_type === 'roles' && (
                <div className="p-3 bg-slate-50 rounded-lg flex flex-wrap gap-4">
                  {[['commercial', 'Commerciaux (AC)'], ['rh', 'RH'], ...(isSA ? [['soa', 'SOA']] : [])].map(([r, lbl]) => (
                    <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={challengeForm.target_roles.includes(r)}
                        onCheckedChange={() => setChallengeForm(prev => ({ ...prev, target_roles: prev.target_roles.includes(r) ? prev.target_roles.filter(x => x !== r) : [...prev.target_roles, r] }))}
                        data-testid={`challenge-role-${r}`}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsChallengeOpen(false)}>Annuler</Button>
            <Button onClick={saveChallenge} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={isNewsOpen} onOpenChange={setIsNewsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNews ? 'Modifier l\'actualite' : 'Nouvelle actualite'}</DialogTitle>
            <DialogDescription>
              Publiez une actualite a tous les utilisateurs ou ciblez un groupe specifique
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                  placeholder="Titre de l'actualite"
                />
              </div>
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select
                  value={newsForm.category}
                  onValueChange={(v) => setNewsForm({ ...newsForm, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="commission">Commission</SelectItem>
                    <SelectItem value="tarif">Tarif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Contenu *</Label>
              <Textarea
                value={newsForm.content}
                onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                placeholder="Contenu de l'actualite..."
                rows={4}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_important"
                checked={newsForm.is_important}
                onCheckedChange={(checked) => setNewsForm({ ...newsForm, is_important: checked })}
              />
              <Label htmlFor="is_important" className="cursor-pointer">
                Marquer comme importante (affichage prioritaire)
              </Label>
            </div>

            {/* Targeting Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                <Send className="w-4 h-4" />
                Destinataires
              </Label>
              
              <div className="space-y-3">
                {isSA && (
                  <div className="space-y-1">
                    <Label className="text-sm text-slate-600">Filiale cible</Label>
                    <Select
                      value={newsForm.target_agency_id || 'all'}
                      onValueChange={(v) => setNewsForm({ ...newsForm, target_agency_id: v === 'all' ? '' : v })}
                    >
                      <SelectTrigger data-testid="news-agency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les filiales</SelectItem>
                        {agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Select
                  value={newsForm.target_type}
                  onValueChange={(v) => setNewsForm({ ...newsForm, target_type: v, target_roles: [], target_users: [], target_rh_commercials: '' })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les utilisateurs</SelectItem>
                    <SelectItem value="roles">Par role (RH, Commercial{isSA ? ', SOA' : ''})</SelectItem>
                    <SelectItem value="rh_commercials">Commerciaux d'un RH specifique</SelectItem>
                    <SelectItem value="specific">Utilisateurs specifiques</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Target by roles */}
                {newsForm.target_type === 'roles' && (
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="text-sm text-slate-600">Selectionner les roles:</Label>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="role_commercial"
                          checked={newsForm.target_roles.includes('commercial')}
                          onCheckedChange={() => toggleRole('commercial')}
                        />
                        <Label htmlFor="role_commercial" className="cursor-pointer">Commerciaux (AC)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="role_rh"
                          checked={newsForm.target_roles.includes('rh')}
                          onCheckedChange={() => toggleRole('rh')}
                        />
                        <Label htmlFor="role_rh" className="cursor-pointer">RH</Label>
                      </div>
                      {isSA && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="role_soa"
                            checked={newsForm.target_roles.includes('soa')}
                            onCheckedChange={() => toggleRole('soa')}
                          />
                          <Label htmlFor="role_soa" className="cursor-pointer">Sous-Admins (SOA)</Label>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Target by RH's commercials */}
                {newsForm.target_type === 'rh_commercials' && (
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <Label className="text-sm text-slate-600">Selectionner un RH:</Label>
                    <Select
                      value={newsForm.target_rh_commercials}
                      onValueChange={(v) => setNewsForm({ ...newsForm, target_rh_commercials: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Choisir un RH" /></SelectTrigger>
                      <SelectContent>
                        {rhUsers.map(rh => (
                          <SelectItem key={rh.user_id} value={rh.user_id}>{rh.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      L'actualite sera envoyee uniquement aux commerciaux rattaches a ce RH
                    </p>
                  </div>
                )}
                
                {/* Target specific users */}
                {newsForm.target_type === 'specific' && (
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <Label className="text-sm text-slate-600">Selectionner les utilisateurs:</Label>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {allUsers.filter(u => u.role !== 'sa').map(u => (
                        <div key={u.user_id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`user_${u.user_id}`}
                            checked={newsForm.target_users.includes(u.user_id)}
                            onCheckedChange={() => toggleUser(u.user_id)}
                          />
                          <Label htmlFor={`user_${u.user_id}`} className="cursor-pointer text-sm">
                            {u.name} 
                            <Badge variant="outline" className="ml-2 text-xs">
                              {u.role === 'soa' ? 'SOA' : u.role === 'rh' ? 'RH' : 'AC'}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewsOpen(false)}>Annuler</Button>
            <Button onClick={saveNews} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
